import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AppContext';
import { Building2, Users, UserCheck, ShieldCheck, TrendingUp, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

export default function SuperAdminDashboard() {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/superadmin/dashboard')
      .then(r => setStats(r.data))
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="flex-center" style={{ height: 300 }}><span className="spinner" style={{ width: 36, height: 36 }} /></div>;

  const chartData = [
    { name: 'Panels', value: stats?.panels || 0, color: '#6366f1' },
    { name: 'Admins', value: stats?.admins || 0, color: '#10b981' },
    { name: 'Employees', value: stats?.subAdmins || 0, color: '#f59e0b' },
    { name: 'Roles', value: stats?.roles || 0, color: '#ef4444' },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Welcome back, {user?.name?.split(' ')[0]} 👋</h1>
          <p className="page-subtitle">Here's what's happening across your system today</p>
        </div>
        <Link to="/superadmin/panels" className="btn btn-primary"><Building2 size={15} /> New Panel</Link>
      </div>

      {/* Stat Cards */}
      <div className="grid-4 mb-6">
        {[
          { label: 'Total Panels', value: stats?.panels || 0, icon: Building2, color: '#6366f1', link: '/superadmin/panels' },
          { label: 'Total Admins', value: stats?.admins || 0, icon: Users, color: '#10b981', link: '/superadmin/admins' },
          // { label:'Employees',  value: stats?.subAdmins || 0, icon: UserCheck,    color:'#f59e0b', link:'/superadmin/admins' },
          { label: 'Active Roles', value: stats?.roles || 0, icon: ShieldCheck, color: '#ef4444', link: '/superadmin/admins' },
        ].map(({ label, value, icon: Icon, color, link }) => (
          <Link key={label} to={link} style={{ textDecoration: 'none' }}>
            <div className="stat-card">
              <div className="stat-icon-wrap" style={{ background: `${color}18` }}>
                <Icon size={22} color={color} />
              </div>
              <div>
                <div className="stat-value">{value}</div>
                <div className="stat-label">{label}</div>
                <div className="stat-change neutral" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                  <ArrowRight size={12} /> View all
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid-2">
        {/* Chart */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 700 }}>System Overview</h2>
            <TrendingUp size={18} color="var(--brand)" />
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} barSize={36}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <YAxis hide allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                cursor={{ fill: 'var(--bg-hover)' }}
              />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {chartData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Recent Admins */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 700 }}>Recent Admins</h2>
            <Link to="/superadmin/admins" style={{ fontSize: '.78rem', color: 'var(--brand)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
              View all <ArrowRight size={13} />
            </Link>
          </div>
          {!stats?.recentAdmins?.length ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '.875rem' }}>No admins yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {stats.recentAdmins.map((a, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div className="user-avatar" style={{ width: 34, height: 34, fontSize: '.75rem' }}>
                    {a.name?.split(' ')?.map(n => n[0])?.join('')?.slice(0, 2)?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                    <div className="text-muted text-sm">{a.panels || 'No panels assigned'}</div>
                  </div>
                  <span className={`role-badge ${a.role_type === 'panel_admin' ? 'role-panel-admin' : 'role-manage-admin'}`}>
                    {a.role_type === 'panel_admin' ? 'Panel' : 'Manage'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
